import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MailCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LogoSwipRent from "@/components/brand/logo_swiprent";
import InputField from "@/components/forms/input_field";
import ButtonMain from "@/components/buttons/button_main";
import GoogleButton from "@/components/auth/google_button";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export default function LoginPage() {
  const { login, confirmOtp, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Two-step login: after a correct password the backend emails a one time
  // code; { otpToken, emailHint } switches this page to the code screen.
  const [otp, setOtp] = useState(null);
  const [code, setCode] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [resending, setResending] = useState(false);
  const codeRef = useRef(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  // Returning from Google OAuth: ?login=ok means the session cookie is set.
  const googleHandled = useRef(false);
  useEffect(() => {
    if (searchParams.get("login") !== "ok" || googleHandled.current) return;
    googleHandled.current = true;
    (async () => {
      await refresh();
      toast.success("Welcome back");
      navigate(location.state?.from || "/swipe", { replace: true });
    })();
  }, [searchParams, refresh, navigate, location.state]);

  useEffect(() => {
    if (otp) codeRef.current?.focus();
  }, [otp]);

  function goAfterLogin(user) {
    const dest = location.state?.from || (user?.role === "lister" || user?.role === "realtor" ? "/lister" : "/swipe");
    navigate(dest, { replace: true });
  }

  async function onSubmit(values) {
    try {
      const result = await login(values.email, values.password);
      if (result?.otpRequired) {
        setOtp({ token: result.otpToken, hint: result.emailHint });
        return;
      }
      goAfterLogin(result);
    } catch (error) {
      toast.warning(formatApiError(error));
    }
  }

  async function onConfirmCode(e) {
    e.preventDefault();
    const clean = code.replace(/\D/g, "");
    if (clean.length !== 6) {
      toast.warning("Enter the 6 digit code from your email.");
      return;
    }
    setConfirming(true);
    try {
      const user = await confirmOtp(otp.token, clean);
      goAfterLogin(user);
    } catch (error) {
      toast.warning(formatApiError(error));
    } finally {
      setConfirming(false);
    }
  }

  async function resend() {
    setResending(true);
    try {
      await api.post("/auth/otp/resend", { otp_token: otp.token });
      toast.success("We sent you a new code. If you do not see it, check your spam folder.");
    } catch (error) {
      toast.warning(formatApiError(error));
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="min-h-screen bg-day lg:flex">
      <div className="flex min-h-screen w-full items-center justify-center px-4 py-10 lg:w-1/2">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full max-w-md rounded-3xl border border-slate-100 bg-white p-8 shadow-sm"
      >
        <Link to="/" className="inline-block">
          <LogoSwipRent />
        </Link>

        {otp ? (
          <>
            <div className="mt-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-tint">
              <MailCheck size={22} className="text-brand-lime" />
            </div>
            <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-brand-ink">Check your email</h1>
            <p className="mt-1 text-sm font-semibold text-slate-500">
              We sent a 6 digit sign in code to {otp.hint || "your email"}. It expires in 10 minutes.
              If you do not see it, check your spam or junk folder.
            </p>
            <form onSubmit={onConfirmCode} className="mt-7 space-y-4">
              <input
                ref={codeRef}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                className="w-full rounded-2xl border border-brand-line bg-white px-4 py-3.5 text-center font-mono text-2xl font-bold tracking-[0.5em] text-brand-ink outline-none transition focus:border-brand-primary"
              />
              <ButtonMain type="submit" full disabled={confirming || code.length !== 6}>
                {confirming ? "Checking" : "Sign in"}
              </ButtonMain>
            </form>
            <div className="mt-5 flex items-center justify-between text-sm font-semibold">
              <button
                type="button"
                onClick={() => { setOtp(null); setCode(""); }}
                className="text-slate-500 transition hover:text-brand-ink"
              >
                Use a different account
              </button>
              <button
                type="button"
                onClick={resend}
                disabled={resending}
                className="text-brand-lime transition hover:text-brand-lime disabled:opacity-50"
              >
                {resending ? "Sending" : "Resend code"}
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className="mt-6 text-2xl font-extrabold tracking-tight text-brand-ink">Welcome back</h1>
            <p className="mt-1 text-sm font-semibold text-slate-500">Log in to keep swiping toward your next home.</p>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-4">
              <InputField label="Email" type="email" placeholder="you@example.com" error={errors.email?.message} {...register("email")} />
              <InputField label="Password" type="password" placeholder="••••••••" error={errors.password?.message} {...register("password")} />
              <div className="flex justify-end">
                <Link
                  to="/auth/forgot-password"
                  className="text-sm font-semibold text-brand-lime transition hover:text-brand-lime"
                >
                  Forgot password?
                </Link>
              </div>
              <ButtonMain type="submit" full disabled={isSubmitting}>
                {isSubmitting ? "Logging in" : "Log in"}
              </ButtonMain>
              <GoogleButton />
            </form>

            <p className="mt-6 text-center text-sm font-semibold text-slate-500">
              New to SwipRent?{" "}
              <Link to="/auth/signup" className="font-extrabold text-brand-lime transition hover:text-brand-lime">
                Create an account
              </Link>
            </p>
          </>
        )}
      </motion.div>
      </div>

      {/* Desktop-only photo panel (right half). Drop a Vancouver / houses photo
          at frontend/public/brand/auth-side.jpg to fill it; until then the warm
          brand gradient shows so it never looks broken. */}
      <aside
        className="relative hidden lg:block lg:w-1/2"
        style={{ background: "linear-gradient(160deg, #F0653E 0%, #1E2A38 100%)" }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/brand/login.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-12 text-white">
          <h2 className="text-[32px] font-extrabold leading-tight tracking-tight">
            Find a place that<br />feels like home.
          </h2>
          <p className="mt-3 max-w-sm text-[15px] text-white/85">
            Swipe through real rentals across Canada, match with the people behind them, and settle in.
          </p>
        </div>
      </aside>
    </div>
  );
}
